import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import DeleteConfirmModal from "./delete-confirm-modal";
import { adminHackDeleteAction, adminHacksListAction } from "../Action/action";

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  const s = String(value);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function hackStatusLabel(h) {
  const active = h?.active !== undefined && h?.active !== null ? Number(h.active) : null;
  if (active === 1) return "Published";
  // Infer "Archived" if explicitly inactive and publish date is in the past
  const pd = h?.publish_date || null;
  if (pd) {
    const t = new Date(pd);
    if (!Number.isNaN(t.getTime())) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      t.setHours(0, 0, 0, 0);
      if (t < today) return "Archived";
    }
  }
  return "Draft";
}

export default function CoachingHacks() {
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("All");
  const [page, setPage] = useState(1);
  const [perPage] = useState(10);
  const [reloadNonce, setReloadNonce] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10 });
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setPage(1);
  }, [query, status]);

  useEffect(() => {
    let cancelled = false;
    const statusParam =
      status === "Published" ? "published" : status === "Draft" ? "draft" : status === "Archived" ? "archived" : undefined;
    const searchParam = query.trim() ? query.trim() : undefined;

    const t = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const res = await adminHacksListAction({
          page,
          per_page: perPage,
          search: searchParam,
          status: statusParam
        });
        if (cancelled) return;
        if (!res?.success) {
          setRows([]);
          setMeta({ total: 0, page, per_page: perPage });
          setError(res?.message || res?.msg || "Failed to load hacks.");
          return;
        }
        setRows(Array.isArray(res.data) ? res.data : []);
        setMeta({
          total: Number(res?.meta?.total || 0),
          page: Number(res?.meta?.page || page),
          per_page: Number(res?.meta?.per_page || perPage)
        });
      } catch (e) {
        if (cancelled) return;
        setRows([]);
        setMeta({ total: 0, page, per_page: perPage });
        setError(e?.response?.data?.message || e?.message || "Failed to load hacks.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [page, perPage, query, status, reloadNonce]);

  const viewRows = useMemo(() => {
    return rows.map((h) => ({
      raw: h,
      id: h?.id,
      title: h?.title || "—",
      category: h?.category || "—",
      statusLabel: hackStatusLabel(h),
      favorites: Number(h?.favorites_count) || 0,
      views: Number(h?.views_count) || 0,
      created: formatDate(h?.created_at)
    }));
  }, [rows]);

  const total = meta?.total ? Number(meta.total) : viewRows.length;
  const totalPages = total ? Math.max(1, Math.ceil(total / perPage)) : 1;
  const start = total && viewRows.length ? (page - 1) * perPage + 1 : 0;
  const end = total && viewRows.length ? start + viewRows.length - 1 : 0;

  const pageButtons = useMemo(() => {
    const max = 5;
    const safeTotal = Math.max(1, totalPages);
    const current = Math.min(Math.max(1, page), safeTotal);
    let s = Math.max(1, current - 2);
    let e = Math.min(safeTotal, s + max - 1);
    s = Math.max(1, e - max + 1);
    const arr = [];
    for (let i = s; i <= e; i += 1) arr.push(i);
    return arr;
  }, [page, totalPages]);

  const openDelete = (row) => {
    if (!row?.id) return;
    setDeleteRow(row);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);
    try {
      const res = await adminHackDeleteAction(deleteRow.id);
      if (!res?.success) throw new Error(res?.message || res?.msg || "Delete failed");
      toast.success("Hack deleted", { toastId: "hack-delete" });
      setDeleteOpen(false);
      setDeleteRow(null);
      setReloadNonce((n) => n + 1);
    } catch (e) {
      toast.error("Delete failed", { toastId: "hack-delete" });
    } finally {
      setDeleting(false);
    }
  };

  const badgeFor = (s) => {
    if (s === "Published") return "badge-success";
    if (s === "Draft") return "badge-warning";
    if (s === "Archived") return "badge-gray";
    return "badge-info";
  };

  const clearAndReload = () => {
    setQuery("");
    setStatus("All");
    setPage(1);
    setError("");
    setReloadNonce((n) => n + 1);
  };

  return (
    <div className="content">
      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Hacks data unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="toolbar">
        <div className="flex items-center gap-3">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search hacks by title..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="filter-group">
            {["All", "Published", "Draft", "Archived"].map((v) => (
              <button
                key={v}
                className={`filter-btn ${status === v ? "active" : ""}`}
                type="button"
                onClick={() => setStatus(v)}
              >
                {v}
              </button>
            ))}
          </div>
          <button className="btn btn-outline" type="button" onClick={clearAndReload} disabled={loading}>
            ↻ Clear
          </button>
        </div>
        <Link to="/coaching-hacks/new" className="btn btn-primary">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 5v14" />
            <path d="M5 12h14" />
          </svg>
          Add New Hack
        </Link>
      </div>

      <div className="card">
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Category</th>
                <th>Status</th>
                <th>Favourites</th>
                <th>Views</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state" style={{ padding: "28px 12px" }}>
                      Loading…
                    </div>
                  </td>
                </tr>
              ) : viewRows.map((h) => (
                <tr key={h.id}>
                  <td>
                    <strong>{h.title}</strong>
                  </td>
                  <td>
                    <span className="badge badge-info">{h.category}</span>
                  </td>
                  <td>
                    <span className={`badge ${badgeFor(h.statusLabel)}`}>{h.statusLabel}</span>
                  </td>
                  <td>{h.favorites ?? "—"}</td>
                  <td>{h.views ?? "—"}</td>
                  <td>{h.created}</td>
                  <td>
                    <div className="actions-cell">
                      <Link to={`/coaching-hacks/${h.id}/edit`} className="action-link">
                        Edit
                      </Link>
                      <button
                        type="button"
                        className="action-link text-danger"
                        onClick={() => openDelete(h)}
                        style={{ background: "transparent", border: "none", padding: 0 }}
                      >
                        Delete
                      </button>
                  
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && viewRows.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="empty-state">
                      <div className="empty-icon">🫥</div>
                      No hacks match your filters.
                    </div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <div className="pagination">
          <span>
            Showing {start}-{end} of {total} Hacks
          </span>
          <div className="pagination-pages">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page <= 1 || loading}
              title="First page"
              aria-label="First page"
            >
              {"<<"}
            </button>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>
              ‹
            </button>
            {pageButtons.map((p) => (
              <button
                key={p}
                className={p === page ? "active" : ""}
                type="button"
                onClick={() => setPage(p)}
                disabled={loading}
              >
                {p}
              </button>
            ))}
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading}>
              ›
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page >= totalPages || loading}
              title="Last page"
              aria-label="Last page"
            >
              {">>"}
            </button>
          </div>
        </div>
      </div>

      <DeleteConfirmModal
        open={deleteOpen}
        itemType="hack"
        itemName={deleteRow?.title || "Hack"}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
          setDeleteRow(null);
        }}
        onConfirm={confirmDelete}
        confirmDisabled={deleting}
      />
    </div>
  );
}

